/**
 * App entry point.
 *
 * MSAL is initialized and the redirect hand-back is drained *before* the first render.
 * MSAL refuses any API call on an uninitialized instance, and a component that mounted
 * mid-redirect would read "signed out" and paint the sign-in gate over a sign-in that is
 * in fact completing. Nothing renders until both settle.
 *
 * Local development is the exception: the portal is gated on sign-in, and a developer
 * working on the request wizard or the case list should not have to complete an Entra
 * redirect to reach them. Under `npm run dev` none of the above runs and
 * `DevAuthProvider` stands in for `MsalProvider` + `AuthProvider`.
 * `npm run dev:npe` serves the same app with the real sign-in flow against NPE.
 */
import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { FluentProvider, Spinner, webLightTheme } from "@fluentui/react-components";
import { MsalProvider } from "@azure/msal-react";
import { Provider } from "react-redux";
import { App } from "./App";
import { store } from "./store";
import { ToastProvider } from "@/fluent-adapter";
import { msalInstance } from "@/config/authConfig";
import { AuthProvider, DevAuthProvider } from "@/features/auth";
import { describeAuthError } from "@/features/auth/authErrors";
import { setAuthError } from "@/features/auth/store/authSlice";

/**
 * Whether to stand in a fake account instead of signing in. True only while the vite dev
 * server is serving in `development` mode — `npm run dev`.
 *
 * Substituted by vite's `define` (see vite.config.ts) from its own `command`, not read
 * from the environment, and declared here because `define` is a build-time substitution
 * TypeScript knows nothing about. `import.meta.env.DEV` would not do: it follows
 * `NODE_ENV`, which vite honours when preset, so `NODE_ENV=development vite build --mode
 * prod` would compile this bypass into a deployable bundle. A build cannot reach it
 * whatever the environment says, and `vite build` now refuses a non-production `NODE_ENV`
 * outright.
 *
 * `npm run dev:npe` serves in `npe` mode, so it takes the real MSAL path below: that is
 * how the sign-in flow and the tenant check are exercised locally.
 *
 * In a build this is a literal `false`, so `DevAuthProvider` is tree-shaken out of the
 * NPE / PPE / PROD bundles rather than merely left unreferenced.
 */
declare const __AUTH_BYPASS__: boolean;

const authDisabled = __AUTH_BYPASS__;

const appTree = (
  <ToastProvider>
    <App />
  </ToastProvider>
);

function Bootstrapper() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    msalInstance
      .initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then((response) => {
        // A redirect response names the account that just signed in; otherwise fall back
        // to a cached one so a reload within the session stays signed in.
        const account = response?.account ?? msalInstance.getAllAccounts()[0] ?? null;
        if (account) {
          msalInstance.setActiveAccount(account);
        }
      })
      .catch((error) => {
        // The redirect hand-back is where a cancelled sign-in, a declined consent prompt
        // or a Conditional Access block lands. Reported into the slice so `SignInPage`
        // can say so; the app still mounts signed-out either way.
        console.error("MSAL initialization failed:", error);
        store.dispatch(setAuthError(describeAuthError(error)));
      })
      .finally(() => {
        setIsInitialized(true);
      });
  }, []);

  if (!isInitialized) {
    return <Spinner size="large" label="Loading" />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>{appTree}</AuthProvider>
    </MsalProvider>
  );
}

function DevBootstrapper() {
  return <DevAuthProvider>{appTree}</DevAuthProvider>;
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root container not found");
}

createRoot(container).render(
  <StrictMode>
    <FluentProvider theme={webLightTheme}>
      <Provider store={store}>{authDisabled ? <DevBootstrapper /> : <Bootstrapper />}</Provider>
    </FluentProvider>
  </StrictMode>,
);
